import React, { useMemo } from 'react'
import { Drawer, Button, Tag } from 'antd'
import styled from 'styled-components'
import moment from 'moment'
import {
  readInviteProgressAttempt,
  readInviteHistory,
  getInviteResendGate,
} from './inviteProgressStorage'
import { INVITE_RECOVERY, inviteSupportMailto } from './inviteRecoveryCopy'

const ORDERING_DEFINITIONS = {
  active: 'Ordered in the last 30 days.',
  inactive: 'Last order was more than 30 days ago.',
  no_orders: 'Connected (or invited) but has not placed an order yet.',
}

const formatDateTime = value => {
  if (!value) return null
  const m = moment(value)
  return m.isValid() ? m.format('DD MMM YYYY, HH:mm') : null
}

const formatDate = value => {
  if (!value) return null
  const m = moment(value)
  return m.isValid() ? m.format('DD MMM YYYY') : null
}

const emailDispatchLabel = dispatch => {
  if (dispatch === 'sent')
    return 'Email handed to provider (not inbox delivery)'
  if (dispatch === 'queued') return 'Email queued (not delivered yet)'
  if (dispatch === 'failed') return 'Email could not be sent'
  return null
}

/**
 * Detailed customer progress — invitation/access vs ordering kept separate.
 * Only shows milestones that can be measured reliably.
 */
const CustomerProgressDrawer = ({
  visible,
  customer,
  orderingStatus,
  onClose,
  onViewUsers,
  onEdit,
  onResend,
}) => {
  const venueId = customer?._id || customer?.venueId
  const lastAttempt = useMemo(
    () => (venueId ? readInviteProgressAttempt(venueId) : null),
    [venueId, visible],
  )
  const inviteHistory = useMemo(
    () => (venueId ? readInviteHistory(venueId) : []),
    [venueId, visible, lastAttempt],
  )
  const resendGate = useMemo(
    () =>
      venueId
        ? getInviteResendGate(venueId)
        : { allowed: true, waitSeconds: 0 },
    [venueId, visible, lastAttempt],
  )

  if (!customer) {
    return (
      <Drawer
        title='Customer progress'
        visible={visible}
        onClose={onClose}
        width={420}
        destroyOnClose
      />
    )
  }

  const recipient =
    lastAttempt?.email || customer.emailAddress || customer.contactEmail || null
  const addedDate = formatDate(
    customer.onboardedDate || customer.createdAt || null,
  )
  const lastOrder = formatDate(
    customer.lastOrderDisplay || customer.orderStats?.lastOrder || null,
  )
  const orderCount = customer.orderStats?.orderCount
  const statusKey = orderingStatus?.key || 'no_orders'
  const statusLabel = orderingStatus?.label || 'No orders yet'
  const statusColor = orderingStatus?.color || '#94a3b8'
  const supportHref = inviteSupportMailto({
    venueName: customer.venueTitle,
    accountNumber: customer.accountNumber,
    email: recipient,
  })
  const canOfferResend = Boolean(
    onResend && lastAttempt && lastAttempt.journey !== 'new',
  )

  return (
    <Drawer
      title='Customer progress'
      visible={visible}
      onClose={onClose}
      width={440}
      destroyOnClose
    >
      <Section>
        <SectionTitle>Customer</SectionTitle>
        <Row>
          <Key>Venue</Key>
          <Val>{customer.venueTitle || '—'}</Val>
        </Row>
        <Row>
          <Key>Account number</Key>
          <Val>{customer.accountNumber || '—'}</Val>
        </Row>
        <Row>
          <Key>Price file</Key>
          <Val>
            {[customer.titlePriceFile, customer.secondTitlePriceFile]
              .filter(Boolean)
              .join(' ') || '—'}
          </Val>
        </Row>
        <Row>
          <Key>Recipient</Key>
          <Val>{recipient || 'Not available'}</Val>
        </Row>
        <Row>
          <Key>Added on OMS</Key>
          <Val>{addedDate || 'Not available'}</Val>
        </Row>
      </Section>

      <Section>
        <SectionTitle>Invitation</SectionTitle>
        {lastAttempt ? (
          <>
            <Row>
              <Key>Last attempt</Key>
              <Val>{formatDateTime(lastAttempt.at) || '—'}</Val>
            </Row>
            <Row>
              <Key>Recipient</Key>
              <Val>{lastAttempt.email || recipient || '—'}</Val>
            </Row>
            <Row>
              <Key>Outcome</Key>
              <Val>
                {emailDispatchLabel(lastAttempt.emailDispatch) ||
                  lastAttempt.emailDispatch ||
                  '—'}
              </Val>
            </Row>
            {inviteHistory.length > 1 && (
              <HistoryList>
                {inviteHistory
                  .slice()
                  .reverse()
                  .slice(0, 5)
                  .map((item, idx) => (
                    <li key={`${item.at}-${idx}`}>
                      {formatDateTime(item.at) || '—'}
                      {' — '}
                      {item.resent ? 'Resend' : 'Send'}
                      {': '}
                      {emailDispatchLabel(item.emailDispatch) ||
                        item.emailDispatch ||
                        '—'}
                    </li>
                  ))}
              </HistoryList>
            )}
            <Note>
              Provider acceptance is not the same as the email arriving in their
              inbox.
            </Note>
          </>
        ) : (
          <Unavailable>{INVITE_RECOVERY.HISTORY_UNAVAILABLE}</Unavailable>
        )}
      </Section>

      <Section>
        <SectionTitle>Access</SectionTitle>
        <Unavailable>
          Venue access isn&apos;t tracked as a separate milestone yet. Opening
          an email or a general login is not the same as using this venue.
        </Unavailable>
        <Actions>
          <Button size='small' onClick={() => onViewUsers?.(customer)}>
            View users
          </Button>
        </Actions>
      </Section>

      <Section>
        <SectionTitle>Ordering activity</SectionTitle>
        <Row>
          <Key>Status</Key>
          <Val>
            <Tag color={statusColor} style={{ margin: 0 }}>
              {statusLabel}
            </Tag>
          </Val>
        </Row>
        <Note>{ORDERING_DEFINITIONS[statusKey]}</Note>
        <Row>
          <Key>Orders (selected range)</Key>
          <Val>{typeof orderCount === 'number' ? orderCount : '—'}</Val>
        </Row>
        <Row>
          <Key>Last / first order signal</Key>
          <Val>
            {lastOrder
              ? `Last order ${lastOrder}`
              : 'No orders yet — this is not the same as no account access'}
          </Val>
        </Row>
        <Note>
          An invitation does not mean commission earned. Ordering status is
          separate from invitation status.
        </Note>
      </Section>

      <Section>
        <SectionTitle>Next action</SectionTitle>
        <Actions>
          <Button
            type='primary'
            size='small'
            onClick={() => onViewUsers?.(customer)}
          >
            View users
          </Button>
          <Button size='small' onClick={() => onEdit?.(customer)}>
            Edit venue
          </Button>
          {canOfferResend && (
            <Button size='small' onClick={() => onResend(customer)}>
              Resend invitation
            </Button>
          )}
        </Actions>
        {canOfferResend && !resendGate.allowed && (
          <Note>{INVITE_RECOVERY.RESEND_TOO_SOON(resendGate.waitSeconds)}</Note>
        )}
        <Note>
          Resend always confirms the recipient first. Changing the recipient or
          transferring ownership is not available here.
        </Note>
        <Support>
          Need help? <a href={supportHref}>Contact OMS support</a> (
          {INVITE_RECOVERY.SUPPORT_EMAIL}).
        </Support>
      </Section>
    </Drawer>
  )
}

const Section = styled.div`
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h4`
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;
`

const Key = styled.span`
  color: #64748b;
  flex-shrink: 0;
`

const Val = styled.span`
  color: #0f172a;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
`

const Note = styled.p`
  margin: 4px 0 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`

const Unavailable = styled.p`
  margin: 0 0 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`

const HistoryList = styled.ul`
  margin: 8px 0;
  padding-left: 18px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
`

const Support = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  color: #64748b;

  a {
    color: #2563eb;
  }
`

export default CustomerProgressDrawer
